<template>
	<div class="outer-container">
		<!-- Main Content -->
		<div class="cropper-container" :class="{ mobile: isMobile }">
			<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
			<cropper
				ref="cropper"
				class="cropper"
				:class="{ mobile: isMobile }"
				:src="img"
				:stencil-component="`${stencilType}-stencil`"
				:stencil-props="{
					handlers: {},
					movable: false,
					resizable: false,
					aspectRatio: aspectRatio,
					lines: { north: false, west: false, south: false, east: false },
				}"
				:stencilSize="stencilSize"
				foregroundClass="vue-advanced-cropper-foreground"
				backgroundClass="vue-advanced-cropper-background"
				:resize-image="{ adjustStencil: false }"
				image-restriction="stencil"
				:transitions="false"
				:debounce="false"
				:min-width="100"
				:min-height="100"
				@change="onChange"
			/>
		</div>
		<Navigation v-if="showNavigation" :zoom="zoom" @change="onZoom" />
	</div>
</template>
<script>
	import { Cropper } from 'vue-advanced-cropper'
	import 'vue-advanced-cropper/dist/style.css'
	import Navigation from './Navigation'

	export default {
		components: {
			Cropper,
			Navigation,
		},
		props: {
			aspectRatio: { type: Number, default: 1 },
			stencilType: { type: String, default: 'rectangle', validator: value => ['rectangle', 'circle'].includes(value) },
			isMobile: { type: Boolean },
			showNavigation: { type: Boolean },
		},
		data() {
			return {
				img: undefined,
				filename: undefined,
				zoom: 0,
			}
		},
		methods: {
			init(file) {
				this.checkImageDimensions(file).then(result => {
					if (result.valid) {
						this.filename = file.name
						const blobUrl = URL.createObjectURL(file)
						this.img = blobUrl
						this.$emit('ready')
					} else {
						this.$stanNotify({ type: 'error', text: this.$t('Image must be at least 100x100.') })
						this.$emit('failInit')
					}
				})
			},
			checkImageDimensions(file) {
				if (!file || !file.type.startsWith('image/')) {
					return Promise.reject(new Error('Not a valid image file'))
				}

				return createImageBitmap(file)
					.then(bitmap => {
						const { width, height } = bitmap
						if (width >= 100 && height >= 100) {
							return { valid: true, width, height }
						}
						return { valid: false, width, height }
					})
					.catch(() => ({ valid: false, width: 0, height: 0 }))
			},
			crop() {
				const { canvas } = this.$refs.cropper.getResult()
				if (canvas) {
					canvas.toBlob(blob => {
						const file = this.blobToFile(blob, this.filename)
						const blobUrl = URL.createObjectURL(blob)
						this.$emit('imageCropped', { blobUrl, file })
					})
				}
			},
			blobToFile(blob, filename) {
				// Extract the file extension from the input filename
				const extension = filename.split('.').pop()

				// Automatically assign the MIME type based on file extension
				const mimeTypes = {
					jpg: 'image/jpeg',
					jpeg: 'image/jpeg',
					png: 'image/png',
				}

				const mimeType = mimeTypes[extension] || 'application/octet-stream' // Default MIME type if unknown

				// Create a File with the determined filename and MIME type
				const file = new File([blob], filename, { type: mimeType })
				return file
			},
			cancelFile() {
				this.file = undefined
				this.img = undefined
				this.zoom = 0
			},

			// navigator
			stencilSize({ boundaries }) {
				return {
					width: Math.min(boundaries.height, boundaries.width) - 100,
					height: Math.min(boundaries.height, boundaries.width) - 100,
				}
			},
			onChange() {
				const cropper = this.$refs.cropper
				if (cropper) {
					const { coordinates, imageSize } = cropper
					if (imageSize.width / imageSize.height > coordinates.width / coordinates.height) {
						// Determine the position of slider bullet
						// It's 0 if the stencil has the maximum size and it's 1 if the has the minimum size
						this.zoom =
							(cropper.imageSize.height - cropper.coordinates.height) / (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)
					} else {
						this.zoom =
							(cropper.imageSize.width - cropper.coordinates.width) / (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)
					}
				}
			},
			onZoom(value) {
				const cropper = this.$refs.cropper
				if (cropper) {
					if (cropper.imageSize.height < cropper.imageSize.width) {
						const minHeight = cropper.sizeRestrictions.minHeight
						const imageHeight = cropper.imageSize.height
						// Determine the current absolute zoom and the new absolute zoom
						// to calculate the sought relative zoom value
						cropper.zoom((imageHeight - this.zoom * (imageHeight - minHeight)) / (imageHeight - value * (imageHeight - minHeight)))
					} else {
						const minWidth = cropper.sizeRestrictions.minWidth
						const imageWidth = cropper.imageSize.width
						cropper.zoom((imageWidth - this.zoom * (imageWidth - minWidth)) / (imageWidth - value * (imageWidth - minWidth)))
					}
				}
			},
		},
	}
</script>
<style lang="scss" scoped>
	.outer-container {
		height: 100%;
		width: 100%;
	}

	.cropper-container {
		position: relative;
		height: 400px;
		overflow-x: hidden;
		overflow-y: hidden;
		display: flex;
		justify-content: center;

		&.mobile {
			height: 100%;
			overflow-x: visible;
		}
	}

	.cropper {
		position: absolute;
		height: 400px;

		&.mobile {
			height: 100%;
			width: 100%;
		}
	}
</style>

<style lang="scss">
	.vue-advanced-cropper-foreground {
		background: white;
	}
	.vue-advanced-cropper-background {
		background: white;
	}
</style>
